<template>
    <div class="p-6">
        <!-- Header -->
        <div class="mb-6">
            <h1 class="text-3xl font-bold text-rose-800 mb-2">Istoric Exporturi PDF</h1>
            <p class="text-gray-600">Toate PDF-urile generate local din sistem</p>
        </div>

        <!-- Filters -->
        <div class="mb-6 bg-white p-4 rounded-lg shadow">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Max Results -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Număr rezultate</label>
                    <Select data-cy="max-results-select" v-model="maxResults" :options="[25, 50, 100, 200]" placeholder="Selectează numărul de rezultate" class="w-full" showClear />
                </div>

                <div class="flex items-end">
                    <Button data-cy="reload-button" label="Reîncarcă" icon="pi pi-refresh" @click="loadExports" severity="info" />
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-8">
            <ProgressSpinner data-cy="loading-spinner" />
            <p class="mt-4 text-gray-600">Se încarcă datele...</p>
        </div>

        <!-- Error State -->
        <div v-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            <i class="pi pi-exclamation-triangle mr-2"></i>
            {{ error }}
        </div>

        <!-- Statistics -->
        <div v-if="!loading && exports.length > 0" class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div class="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4 rounded-lg shadow">
                <div class="flex items-center gap-3">
                    <i class="pi pi-file-pdf text-3xl"></i>
                    <div>
                        <div class="text-2xl font-bold">{{ exports.length }}</div>
                        <div class="text-sm opacity-90">Total exporturi</div>
                    </div>
                </div>
            </div>

            <div class="bg-gradient-to-br from-green-500 to-green-600 text-white p-4 rounded-lg shadow">
                <div class="flex items-center gap-3">
                    <i class="pi pi-calendar text-3xl"></i>
                    <div>
                        <div class="text-2xl font-bold">{{ todayExports }}</div>
                        <div class="text-sm opacity-90">Astăzi</div>
                    </div>
                </div>
            </div>

            <div class="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-4 rounded-lg shadow">
                <div class="flex items-center gap-3">
                    <i class="pi pi-users text-3xl"></i>
                    <div>
                        <div class="text-2xl font-bold">{{ uniquePatients }}</div>
                        <div class="text-sm opacity-90">Pacienți unici</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Exports List -->
        <div v-if="!loading && exports.length > 0" class="bg-white rounded-lg shadow overflow-hidden">
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Fișier
                        </th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Pacient
                        </th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Data consultație
                        </th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Generat de
                        </th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Data generare
                        </th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="exportRecord in exports" :key="exportRecord.id" class="hover:bg-gray-50">
                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="flex items-center">
                                <i class="pi pi-file-pdf text-red-500 text-xl mr-2"></i>
                                <div class="text-sm font-medium text-gray-900">
                                    {{ exportRecord.fileName }}
                                </div>
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="text-sm font-medium text-gray-900">
                                {{ exportRecord.patientNameSnapshot }}
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="text-sm text-gray-900">
                                {{ exportRecord.dateConsult }}
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="text-sm text-gray-600">
                                {{ exportRecord.createdByEmail }}
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="text-sm text-gray-900">
                                {{ formatDate(exportRecord.createdAt) }}
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <Badge value="Local" severity="success" />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Empty State -->
        <div v-if="!loading && exports.length === 0" class="text-center py-12 bg-gray-50 rounded-lg">
            <i class="pi pi-inbox text-6xl text-gray-400"></i>
            <p class="mt-4 text-gray-600 text-lg">Nu există exporturi</p>
            <p class="text-gray-500 text-sm">PDF-urile generate vor apărea aici</p>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import Select from 'primevue/select'
import Button from 'primevue/button'
import Badge from 'primevue/badge'
import ProgressSpinner from 'primevue/progressspinner'
import { useExportHistory } from '../../composables/exports/useExportHistory.js'

const { getRecentExports } = useExportHistory()

const exports = ref([])
const loading = ref(false)
const error = ref(null)
const maxResults = ref(50)

const todayExports = computed(() => {
    const today = new Date().toISOString().split('T')[0]
    return exports.value.filter(exp => {
        const expDate = new Date(exp.createdAt).toISOString().split('T')[0]
        return expDate === today
    }).length
})

const uniquePatients = computed(() => {
    const patients = new Set(exports.value.map(exp => exp.patientNameSnapshot))
    return patients.size
})

const loadExports = async () => {
    loading.value = true
    error.value = null
    
    try {
        exports.value = await getRecentExports(maxResults.value)
    } catch (err) {
        error.value = err.message
        console.error('Error loading exports:', err)
    } finally {
        loading.value = false
    }
}

const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleString('ro-RO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

onMounted(() => {
    loadExports()
})
</script>
